// components/OnboardingWizard.jsx
// ═══════════════════════════════════════════════════════════════════
// Wizard de configuración inicial del tenant.
// Se muestra cuando el tenant no tiene pais_codigo configurado.
// Paso único: seleccionar país de operación.
// ═══════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { clearFiscalCache } from '../hooks/useFiscal';

const OnboardingWizard = ({ onComplete }) => {
  const [paises, setPaises]         = useState([]);
  const [seleccionado, setSeleccionado] = useState('DO');
  const [guardando, setGuardando]   = useState(false);
  const [cargando, setCargando]     = useState(true);

  useEffect(() => {
    api.get('/configuracion/paises')
      .then(r => setPaises(r.data))
      .catch(() => {
        // Fallback con países básicos si falla el API
        setPaises([
          { codigo:'DO', nombre:'República Dominicana', impuesto_label:'ITBIS', impuesto_tasa:18, moneda_simbolo:'RD$', moneda_codigo:'DOP' },
          { codigo:'CO', nombre:'Colombia',             impuesto_label:'IVA',   impuesto_tasa:19, moneda_simbolo:'COP$', moneda_codigo:'COP' },
          { codigo:'MX', nombre:'México',               impuesto_label:'IVA',   impuesto_tasa:16, moneda_simbolo:'MX$', moneda_codigo:'MXN' },
          { codigo:'PA', nombre:'Panamá',               impuesto_label:'ITBMS', impuesto_tasa:7,  moneda_simbolo:'$',   moneda_codigo:'USD' },
          { codigo:'CR', nombre:'Costa Rica',           impuesto_label:'IVA',   impuesto_tasa:13, moneda_simbolo:'₡',   moneda_codigo:'CRC' },
        ]);
      })
      .finally(() => setCargando(false));
  }, []);

  const confirmar = async () => {
    setGuardando(true);
    try {
      await api.patch('/configuracion/pais', { pais_codigo: seleccionado });
      clearFiscalCache();
      onComplete(seleccionado);
    } catch (e) {
      alert('Error al guardar la configuración. Intenta de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  const paisActual = paises.find(p => p.codigo === seleccionado);

  return (
    <div style={s.overlay}>
      <div style={s.card}>

        {/* Logo / Header */}
        <div style={s.header}>
          <div style={s.logoMarca}>eFIT</div>
          <h1 style={s.titulo}>¡Bienvenido a eFIT!</h1>
          <p style={s.subtitulo}>
            Antes de comenzar, dinos en qué país opera tu empresa.<br/>
            El sistema se configurará automáticamente con las reglas fiscales de tu país.
          </p>
        </div>

        {/* Selector de país */}
        <div style={s.selectorWrap}>
          <label style={s.labelPais}>Selecciona tu país de operación</label>

          {cargando ? (
            <div style={s.loadingText}>Cargando países...</div>
          ) : (
            <div style={s.paisesGrid}>
              {paises.map(p => (
                <button
                  key={p.codigo}
                  onClick={() => setSeleccionado(p.codigo)}
                  style={{
                    ...s.paisBtn,
                    ...(seleccionado === p.codigo ? s.paisBtnActivo : {})
                  }}
                >
                  <span style={s.paisCodigo}>{p.codigo}</span>
                  <span style={s.paisNombre}>{p.nombre}</span>
                  <span style={s.paisImpuesto}>
                    {p.impuesto_label} {p.impuesto_tasa}% · {p.moneda_simbolo} {p.moneda_codigo}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Resumen del país seleccionado */}
        {paisActual && (
          <div style={s.resumen}>
            <p style={s.resumenTitulo}>Lo que se configurará automáticamente:</p>
            <div style={s.resumenGrid}>
              <div style={s.resumenItem}>
                <span style={s.resumenLabel}>Impuesto</span>
                <span style={s.resumenValor}>{paisActual.impuesto_label} {paisActual.impuesto_tasa}%</span>
              </div>
              <div style={s.resumenItem}>
                <span style={s.resumenLabel}>Moneda</span>
                <span style={s.resumenValor}>{paisActual.moneda_simbolo} ({paisActual.moneda_codigo})</span>
              </div>
              <div style={s.resumenItem}>
                <span style={s.resumenLabel}>ID Fiscal</span>
                <span style={s.resumenValor}>{paisActual.id_fiscal_label || '—'}</span>
              </div>
              <div style={s.resumenItem}>
                <span style={s.resumenLabel}>Regulador</span>
                <span style={s.resumenValor}>{paisActual.ente_regulador || '—'}</span>
              </div>
            </div>
          </div>
        )}

        {/* Botón confirmar */}
        <button
          onClick={confirmar}
          disabled={!seleccionado || guardando || cargando}
          style={s.btnConfirmar}
        >
          {guardando ? '⏳ Configurando...' : `✅ Confirmar — ${paisActual?.nombre || seleccionado}`}
        </button>

        <p style={s.nota}>
          Podrás cambiar esto después en <strong>Configuración → País</strong> o desde el panel de SuperAdmin.
        </p>
      </div>
    </div>
  );
};

// ── ESTILOS ───────────────────────────────────────────────────────
const s = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 9999,
    background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 16
  },
  card: {
    background: '#1e293b', borderRadius: 20, padding: '40px 36px',
    maxWidth: 680, width: '100%', border: '1px solid #334155',
    boxShadow: '0 25px 50px rgba(0,0,0,0.5)'
  },
  header: { textAlign: 'center', marginBottom: 32 },
  logoMarca: {
    display: 'inline-block', background: 'linear-gradient(135deg, #6366f1, #3b82f6)',
    color: 'white', fontWeight: 900, fontSize: 28, borderRadius: 12,
    padding: '8px 20px', marginBottom: 16, letterSpacing: 2
  },
  titulo: { margin: '0 0 8px', color: '#f1f5f9', fontSize: 24, fontWeight: 700 },
  subtitulo: { margin: 0, color: '#94a3b8', fontSize: 14, lineHeight: 1.6 },

  selectorWrap: { marginBottom: 24 },
  labelPais: { display: 'block', color: '#94a3b8', fontSize: 13, fontWeight: 600, marginBottom: 12 },
  loadingText: { color: '#64748b', textAlign: 'center', padding: 20 },

  paisesGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10
  },
  paisBtn: {
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4,
    padding: '14px 16px', background: '#0f172a', border: '2px solid #334155',
    borderRadius: 12, cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s'
  },
  paisBtnActivo: {
    border: '2px solid #6366f1', background: '#1e1f4a', boxShadow: '0 0 0 3px rgba(99,102,241,0.2)'
  },
  paisCodigo: { fontSize: 18, fontWeight: 800, color: '#6366f1' },
  paisNombre: { fontSize: 13, fontWeight: 600, color: '#f1f5f9' },
  paisImpuesto: { fontSize: 11, color: '#64748b' },

  resumen: {
    background: '#0f172a', border: '1px solid #334155', borderRadius: 12,
    padding: '16px 20px', marginBottom: 24
  },
  resumenTitulo: { margin: '0 0 12px', color: '#64748b', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 },
  resumenGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px 24px' },
  resumenItem: { display: 'flex', flexDirection: 'column', gap: 2 },
  resumenLabel: { fontSize: 11, color: '#64748b' },
  resumenValor: { fontSize: 14, fontWeight: 700, color: '#a5b4fc' },

  btnConfirmar: {
    width: '100%', padding: '14px 0', fontSize: 16, fontWeight: 700,
    background: 'linear-gradient(135deg, #6366f1, #3b82f6)', color: 'white',
    border: 'none', borderRadius: 12, cursor: 'pointer', marginBottom: 12
  },
  nota: { textAlign: 'center', color: '#475569', fontSize: 12, margin: 0 }
};

export default OnboardingWizard;
